import { useState } from 'react';
import { Button as PennyButton, Container, Group, Text } from '@melio/penny';

import { Card, Icon, Button, Avatar, Mono, NakedButton, Pill, type IconKey } from '@/components/ds';
import { PageTitle, Table } from '@/containers/dashboard';
import { useApiCtx, useAsync, Loading, ErrorBanner } from '@/components/live';
import { listEntities, getEntity } from '@/api/payouts';
import type { Address, Business } from '@/api/types';
import type { Go } from '@/lib/types';

// dashboard-businesses.tsx — the Entities section of the signed-in dashboard. A "business" in the UI
// is an entity in the payouts `/v2` API. Calls authenticate with the user's access token (Bearer)
// via {@link useApiCtx}. The list is fetched from GET /entities; the drill-down lives at its own URL
// (`/dashboard/entities/:id`) and re-fetches the single entity by id (GET /entities/:id) so a detail
// URL is directly linkable/refreshable.

// ---- display helpers ----------------------------------------------------
const DECO = ['var(--decorative-2)', 'var(--decorative-4)', 'var(--decorative-5)', 'var(--decorative-3)', 'var(--decorative-1)'];
const bizName = (b: any): string => b?.name || b?.legalName || b?.org?.name || b?.org?.legalName || b?.id || 'Business';
const bizInitials = (b: any): string =>
  bizName(b)
    .split(/\s+/)
    .slice(0, 2)
    .map((w: string) => w[0])
    .join('')
    .toUpperCase();
const bizColor = (b: any): string => DECO[[...(b?.id || '')].reduce((a: number, c: string) => a + c.charCodeAt(0), 0) % DECO.length];

const fmtDate = (s?: string): string => {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString();
};

/** Full address as newline-separated lines, or `null` when there's nothing to show. */
function AddressLines({ address }: { address?: Address }) {
  if (!address) return <>—</>;
  const cityLine = [address.city, address.state, address.postalCode].filter(Boolean).join(', ');
  const lines = [address.line1, address.line2, cityLine, address.countryCode].filter(Boolean);
  if (lines.length === 0) return <>—</>;
  return (
    <>
      {lines.map((l, i) => (
        <div key={i}>{l}</div>
      ))}
    </>
  );
}

interface DefListProps {
  rows: [string, React.ReactNode][];
  style?: React.CSSProperties;
}

// Two-column definition list: a fixed muted label column and a left-aligned value column,
// hairline dividers between rows. Left-aligning the value (rather than pushing it to the
// right edge) keeps long values — industry, addresses — readable.
function DefList({ rows }: DefListProps) {
  return (
    <Group variant="vertical" spacing="none" hasDivider width="full">
      {rows.map(([k, v]) => (
        <Container key={k} paddingY="xs">
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(96px, 34%) 1fr', gap: 16, alignItems: 'baseline' }}>
            <Text textStyle="body4" color="semantic.text.secondary">
              {k}
            </Text>
            <Text textStyle="body3Semi" style={{ wordBreak: 'break-word' }}>
              {v}
            </Text>
          </div>
        </Container>
      ))}
    </Group>
  );
}

// Small clickable chip that copies its value to the clipboard, with a brief confirmation.
function CopyChip({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      },
      () => {}
    );
  };
  // penny-backed: a tertiary <Button>. Penny's own <Chip> is a selection/filter chip with no
  // click affordance, so the button is the right primitive for "click to copy" — and it brings
  // the hover, focus-visible and pressed states the hand-rolled <button> never had.
  return (
    <PennyButton
      size="small"
      variant="tertiary"
      label={value}
      rightElement={<Icon name={copied ? 'checked' : 'copy'} size={14} />}
      onClick={copy}
      aria-label={copied ? 'Copied' : `Copy ID ${value}`}
      data-testid="entity-id-copy"
      style={{ maxWidth: '100%', fontFamily: "'JetBrains Mono', monospace" }}
    />
  );
}

// ====== ENTITY DETAIL ====================================================
interface CardSectionProps {
  title: string;
  icon?: IconKey;
  children: React.ReactNode;
  style?: React.CSSProperties;
}
function CardSection({ title, icon, children, style }: CardSectionProps) {
  return (
    <Card padding={24} style={style}>
      <Container paddingBottom="xxs">
        <Group variant="horizontal" spacing="xxs" alignItems="center">
          {icon && <Icon name={icon} size={16} color="var(--text-secondary)" />}
          <Text textStyle="caption1SemiUpper" color="semantic.text.secondary">
            {title}
          </Text>
        </Group>
      </Container>
      {children}
    </Card>
  );
}

// Labelled address block used inside the combined Addresses card.
function AddressBlock({ label, address }: { label: string; address?: Address }) {
  return (
    <Group variant="vertical" spacing="xxs">
      <Text textStyle="body4" color="semantic.text.secondary">
        {label}
      </Text>
      <Text textStyle="body3Semi">
        <AddressLines address={address} />
      </Text>
    </Group>
  );
}

// A label/value pair in the entity hero band.
function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <Group variant="vertical" spacing="xxs">
      <Text textStyle="caption1SemiUpper" color="semantic.text.secondary">
        {label}
      </Text>
      <Text textStyle="body4Semi">{value}</Text>
    </Group>
  );
}

interface BusinessDetailProps {
  business: Business;
  onBack?: () => void;
  style?: React.CSSProperties;
}

// Renders every field the entity payload carries. Eligibility is intentionally omitted — the
// /limitations surface isn't authorized for the user access token yet.
function BusinessDetail({ business, onBack }: BusinessDetailProps) {
  const owner = business.owner || {};
  const contactName =
    [business.contact?.firstName ?? business.contactFirstName, business.contact?.lastName ?? business.contactLastName]
      .filter(Boolean)
      .join(' ') || '—';
  const taxId = business.taxInfo?.identifierLast4
    ? `${business.taxInfo.type ? `${business.taxInfo.type.toUpperCase()} ` : ''}••••${business.taxInfo.identifierLast4}`
    : '—';
  const industry = [business.industry?.naicsCode, business.industry?.name].filter(Boolean).join(' · ') || '—';
  const ownerName = [owner.firstName, owner.lastName].filter(Boolean).join(' ') || '—';

  return (
    <div>
      <NakedButton leftIcon="arrow-left" color="var(--text-secondary)" onClick={onBack} style={{ marginBottom: 14 }}>
        All entities
      </NakedButton>

      {/* Hero header — tinted band with avatar, name, type and a copyable id */}
      <Card padding={0} style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div
          style={{
            background: 'var(--surface-primary)',
            padding: '26px 28px',
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            flexWrap: 'wrap',
          }}
        >
          <Avatar initials={bizInitials(business)} color={bizColor(business)} size={56} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <Group variant="horizontal" spacing="s" alignItems="center">
              <Text as="h1" textStyle="heading1Semi">
                {bizName(business)}
              </Text>
              {business.type && <Pill kind="brand">{business.type}</Pill>}
            </Group>
            <Container paddingTop="xs">
              <CopyChip value={business.id} />
            </Container>
          </div>
          <Group variant="horizontal" spacing="xl">
            <HeroStat label="Created" value={fmtDate(business.createdAt)} />
            <HeroStat label="Updated" value={fmtDate(business.updatedAt)} />
          </Group>
        </div>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="grid-2" style={{ alignItems: 'stretch' }}>
          <CardSection title="Business" icon="buildings">
            <DefList
              rows={[
                ['Name', business.name || '—'],
                ['Legal name', business.legalName || '—'],
                ['Business type', business.businessType || '—'],
                ['Tax ID', taxId],
                ['Phone', business.phoneNumber || '—'],
                [
                  'Website',
                  business.website ? (
                    <a href={business.website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
                      {business.website}
                    </a>
                  ) : (
                    '—'
                  ),
                ],
                ['Industry', industry],
              ]}
            />
          </CardSection>
          <CardSection title="Owner" icon="user">
            <DefList
              rows={[
                ['Name', ownerName],
                ['Email', owner.email || '—'],
                ['Date of birth', owner.dateOfBirth || '—'],
                ['Phone', owner.phoneNumber || '—'],
              ]}
            />
          </CardSection>
          <CardSection title="Contact" icon="contacts">
            <DefList rows={[['Name', contactName]]} />
          </CardSection>
          <CardSection title="Overview" icon="info-outline">
            <DefList
              rows={[
                ['Type', business.type || '—'],
                ['External ID', business.externalId ? <Mono size="caption">{business.externalId}</Mono> : '—'],
              ]}
            />
          </CardSection>
        </div>

        <CardSection title="Addresses" icon="home">
          <div className="grid-2">
            <AddressBlock label="Physical address" address={business.address} />
            <AddressBlock label="Legal address" address={business.legalAddress} />
          </div>
        </CardSection>
      </div>
    </div>
  );
}

// ====== ENTITY DETAIL (by id) ============================================
// Re-fetches the single entity by id so the detail URL (`/dashboard/entities/:id`) is directly
// linkable and survives a refresh — it doesn't rely on the list already being in memory.
interface EntityDetailViewProps {
  entityId: string;
  onBack: () => void;
}
function EntityDetailView({ entityId, onBack }: EntityDetailViewProps) {
  const ctx = useApiCtx();
  const { loading, data, error, reload } = useAsync((signal: AbortSignal) => getEntity(ctx, entityId, signal), [ctx.env, entityId]);

  if (loading) return <Loading label="Loading entity…" />;
  if (error) return <ErrorBanner error={error} onRetry={reload} />;
  if (!data) return <ErrorBanner error={new Error('Entity not found.')} onRetry={onBack} />;
  return <BusinessDetail business={data} onBack={onBack} />;
}

// ====== ENTITIES =========================================================
interface BusinessesViewProps {
  entityId?: string;
  go: Go;
}
function BusinessesView({ entityId, go }: BusinessesViewProps) {
  const ctx = useApiCtx();
  // Only fetch the list when we're on the list view; the detail view fetches its entity by id.
  const { loading, data, error, reload } = useAsync((signal: AbortSignal) => listEntities(ctx, signal), [ctx.env], {
    enabled: !entityId,
  });

  if (entityId) return <EntityDetailView entityId={entityId} onBack={() => go('/dashboard/entities')} />;

  const businesses: Business[] = data || [];

  return (
    <div>
      <PageTitle
        title="Entities"
        sub="Every legal entity you onboard and pay from."
        action={
          <Button leftIcon="add" disabled>
            Add entity
          </Button>
        }
      />
      {loading ? (
        <Loading label="Loading entities…" />
      ) : error ? (
        <ErrorBanner error={error} onRetry={reload} />
      ) : businesses.length === 0 ? (
        <Card padding={32} style={{ textAlign: 'center' }}>
          <Text textStyle="body2" color="semantic.text.secondary">
            No entities on this partner account yet.
          </Text>
        </Card>
      ) : (
        <Table
          onRow={(r: Business) => go(`/dashboard/entities/${r.id}`)}
          columns={[
            {
              key: 'name',
              label: 'Entity',
              render: (r: any) => (
                <Group variant="horizontal" spacing="s" alignItems="center">
                  <Avatar initials={bizInitials(r)} color={bizColor(r)} size={30} />
                  <Group variant="vertical" spacing="none">
                    <Text textStyle="body3Semi">{bizName(r)}</Text>
                    <Mono size="caption" color="semantic.text.secondary">
                      {r.id}
                    </Mono>
                  </Group>
                </Group>
              ),
            },
            {
              key: 'businessType',
              label: 'Type',
              render: (r: any) => (
                <Text textStyle="body4" color="semantic.text.secondary">
                  {r.org?.businessType || r.businessType || '—'}
                </Text>
              ),
            },
            { key: 'x', label: '', align: 'right', render: () => <Icon name="chevron-right" size={16} color="var(--neutral-600)" /> },
          ]}
          rows={businesses}
        />
      )}
    </div>
  );
}

export { BusinessesView, BusinessDetail, DefList };
